/**
 * Mixpanel config bootstrap for Meiuca web apps.
 *
 * Mirrors `@meiuca/firebase`'s `firebase.ts`: this module owns the singleton
 * configuration and enabled-state. The actual `mixpanel.init()` call happens in
 * `MixpanelAnalyticsProvider.initialize()`, AFTER the dev gate — so in dev
 * (without the opt-in env) the SDK never loads or phones home.
 */

import mixpanel from 'mixpanel-browser';

export interface MixpanelConfig {
  /** Mixpanel project token (one project per app). */
  token: string;
  /**
   * API host override. Set to `https://api-eu.mixpanel.com` for EU data
   * residency (recommended for LGPD). Leave undefined for the US default.
   */
  apiHost?: string;
  /** Logs Mixpanel SDK activity to the console (dev only). */
  debug?: boolean;
}

let mixpanelConfig: MixpanelConfig | null = null;

/**
 * Stores the Mixpanel configuration. Does NOT init the SDK — the provider does
 * that after the dev gate. Returns false (and disables Mixpanel) if no token.
 */
export const initializeMixpanel = (config: MixpanelConfig): boolean => {
  if (!config.token) {
    console.warn('[MIXPANEL] Missing token, Mixpanel disabled');
    mixpanelConfig = null;
    return false;
  }

  mixpanelConfig = config;
  return true;
};

/** Returns the stored Mixpanel configuration, or null if not configured. */
export const getMixpanelConfig = (): MixpanelConfig | null => mixpanelConfig;

/** Whether Mixpanel has a valid configuration (token present). */
export const isMixpanelEnabled = (): boolean => mixpanelConfig !== null;

/** The Mixpanel browser singleton (used by the provider). */
export const getMixpanel = () => mixpanel;

/** Clears the Mixpanel configuration (useful for tests/reconfiguration). */
export const resetMixpanel = (): void => {
  mixpanelConfig = null;
};
